import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Layout } from '../../components/Layout';
import { LoadingScreen } from '../../components/LoadingScreen';
import { PhoneVerificationModal, isValidPhoneNumber, SELECTORS, CSS_CLASSES, CUSTOM_CLASSES } from '@kui-contrib/phone-verification';

// KUI Foundations CSS - base styles and theme
import '@kui/foundations-css/dist/base/base.css';
import '@kui/foundations-css/dist/themes/kui11.base.css';
import '@kui/foundations-css/dist/themes/kui11.components.css';

// KUI Component styles needed by phone verification.
// The bumped module (post-MR !15) renders with many foundations component
// classes (nv-flex, nv-banner-root, nv-menu-*, nv-input-shell, etc.).
// Without these sheets the OTP digits collapse into a single visible box,
// the "code expires" banner is unstyled, and the country dropdown chrome
// breaks. Keep this list aligned with the classes the module emits.
import '@kui/foundations-css/dist/components/anchor.css';
import '@kui/foundations-css/dist/components/animated-chevron.css';
import '@kui/foundations-css/dist/components/banner.css';
import '@kui/foundations-css/dist/components/button.css';
import '@kui/foundations-css/dist/components/flex.css';
import '@kui/foundations-css/dist/components/form-field.css';
import '@kui/foundations-css/dist/components/input.css';
import '@kui/foundations-css/dist/components/input-shell.css';
import '@kui/foundations-css/dist/components/label.css';
import '@kui/foundations-css/dist/components/menu.css';
import '@kui/foundations-css/dist/components/modal.css';
import '@kui/foundations-css/dist/components/primitive.css';
import '@kui/foundations-css/dist/components/select.css';
import '@kui/foundations-css/dist/components/spinner.css';

// Phone verification component styles
import '@kui-contrib/phone-verification/styles.css';

import '../index.css';
import {
  fetchLoginHintsForApplication,
  getStarfleetLoginHintsFromQueryString,
  persistStarfleetLoginHints,
  restoreStarfleetLoginHints,
} from '../../utils/loginHints';
import { loadPublicAppConfig } from '../../utils/loadPublicAppConfig';
import {
  loadPartnerCountries,
  regionDisplayName,
  toModuleCountryList,
} from '../../utils/prhsCountries';
import {
  clearReauthAttempts,
  clearStoredStarfleetIdTokenIfLoginHintMismatch,
  didStarfleetSessionExpire,
  getStarfleetAuthDisplayProfile,
  getStarfleetIdTokenSubject,
  markStarfleetSessionExpired,
  MAX_SILENT_REAUTH_ATTEMPTS,
  readReauthAttempts,
  readStoredStarfleetIdToken,
  readStoredStarfleetUserInfo,
  recordReauthAttempt,
  redirectToStarfleetAuthorize,
  writeStoredStarfleetIdToken,
} from '../../utils/starfleetAuth';

/**
 * Centralised handler for a 401 from any PRHS API call.
 *
 * The id_token in sessionStorage was valid when the modal mounted but has
 * expired since (or it was rejected by the server for some other reason).
 * Wipe the stored token and reload the page — the OAuth bootstrap effect
 * sees no token and silently re-authenticates via the bundled login hints.
 *
 * Throws so the modal stops the current flow; the throw is mostly cosmetic
 * because the page is about to navigate away.
 */
function handleUnauthorizedAndReload(intl) {
  // Distinguishes this from a visitor who never signed in: if re-auth can't
  // recover the session, the fallback screen says so instead of showing a bare
  // "Sign in required" to someone who was signed in moments ago.
  markStarfleetSessionExpired();
  writeStoredStarfleetIdToken(null);
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
  throw new Error(
    intl.formatMessage({
      id: 'home.error.refreshingSession',
      defaultMessage: 'Refreshing your session…',
    })
  );
}

/** Bearer for PRHS API: id_token from the Starfleet OAuth callback (sessionStorage). */
function getStarfleetBearerToken() {
  return readStoredStarfleetIdToken();
}

/** Env var wins over config so a build can point at a different client. */
function resolveStarfleetOAuthSettings(cfg) {
  return {
    clientId:
      (process.env.REACT_APP_STARFLEET_CLIENT_ID || '').trim() ||
      (cfg?.starfleetClientId || '').trim(),
    loginOrigin: (cfg?.starfleetLoginOrigin || '').trim(),
  };
}

/**
 * Send the browser to Starfleet /authorize. Shared by the bootstrap effect and
 * the fallback screen's retry, so both build the same redirect_uri — it has to
 * match the one registered for the client or the exchange fails.
 *
 * We don't send prompt=none: with a live NVIDIA session Starfleet returns a code
 * immediately (the user sees only a flicker), and without one it renders its own
 * login page, which is the correct outcome rather than an error to handle.
 */
function startStarfleetAuthorize({ cfg, clientId, loginOrigin, hints }) {
  const rawPath = cfg.oauthRedirectPath || '/oauth/callback';
  const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  return redirectToStarfleetAuthorize({
    loginOrigin,
    clientId,
    redirectUri: `${window.location.origin}${path}`,
    loginHint: hints.loginHint,
    idpId: hints.idpId,
  });
}

const UAS_CALLBACK_STORAGE_KEY = 'prhs.uas.callback_url';

/** Read ?uasCallbackUrl= from URL query params (the name UAS hands the browser). */
function getCallbackUrlFromQuery() {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('uasCallbackUrl');
}

/**
 * Persist the callback URL to sessionStorage so it survives the Starfleet
 * OAuth full-page redirect roundtrip (?callback= is gone from URL on return).
 */
function persistCallbackUrl(url) {
  if (typeof window === 'undefined' || !url) return;
  try {
    window.sessionStorage.setItem(UAS_CALLBACK_STORAGE_KEY, url);
  } catch {
    // sessionStorage may be blocked (private mode, ITP); ignore
  }
}

function restoreCallbackUrl() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(UAS_CALLBACK_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearCallbackUrl() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(UAS_CALLBACK_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const RESEND_WINDOW_STORAGE_KEY = 'prhs.otp.resend_window';

/**
 * Carry the resend gate across page loads.
 *
 * PRHS keeps enforcing the cooldown whether or not a browser is still open, but
 * the in-memory gate dies with the page — so a user who reloads mid-wait sees an
 * enabled Send Code and spends a request on a certain OTP_RESEND_TOO_SOON.
 *
 * localStorage, not sessionStorage: the latter dies with the tab, which is the
 * case that leaks. Scoped to the token subject because PRHS keys the cooldown to
 * the caller — a shared browser must not disable Send Code for the next account.
 */
function persistResendWindow(subject, allowedAtMs) {
  if (typeof window === 'undefined' || !subject) return;
  try {
    window.localStorage.setItem(
      RESEND_WINDOW_STORAGE_KEY,
      JSON.stringify({ sub: subject, allowedAtMs })
    );
  } catch {
    // localStorage may be blocked (private mode, ITP); the gate just won't survive a reload
  }
}

/** Stored deadline for `subject`, or 0 when absent, foreign, or already elapsed. */
function restoreResendWindow(subject) {
  if (typeof window === 'undefined' || !subject) return 0;
  try {
    const raw = window.localStorage.getItem(RESEND_WINDOW_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (parsed?.sub !== subject) return 0;
    const allowedAtMs = Number(parsed.allowedAtMs);
    if (!Number.isFinite(allowedAtMs) || allowedAtMs <= Date.now()) {
      // Ours, and we will never honour it again — drop it rather than leave the
      // account id in storage until some later send overwrites it. Only safe
      // below the subject check: a foreign entry may still be live.
      clearPersistedResendWindow();
      return 0;
    }
    return allowedAtMs;
  } catch {
    return 0;
  }
}

function clearPersistedResendWindow() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(RESEND_WINDOW_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Kill switch for the manage-only block in onInit. Absent or anything but a
 * literal `false` keeps the block on, so no config change is needed to ship it.
 *
 * It exists because the block infers "manage surface" from a *missing*
 * uasCallbackUrl, and no test can prove every real registration entry point
 * sends one (the GFN e2e helper supplies the parameter itself). If a genuine
 * registration path ever arrives without it, those users cannot register at all
 * — setting `blockManageEntryWithoutPhone: false` in the runtime config reopens
 * the form for them immediately, without waiting on a release.
 */
function blocksManageEntryWithoutPhone(cfg) {
  return cfg?.blockManageEntryWithoutPhone !== false;
}

// Schemes safe to pass to window.location.assign. Anything else
// (javascript:, data:, file:, about:, …) can execute or read local
// resources and must be rejected even if the rest of the URL looks ok.
const ALLOWED_CALLBACK_SCHEMES = new Set(['https:']);
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Validates a callback URL against the configured origin allowlist.
 *
 * Fails closed: a missing/empty `allowedOrigins` list rejects every
 * callback, so a missing config never silently turns into an open
 * redirect. Each environment must explicitly populate
 * `allowedCallbackOrigins` in its public/config(.{env}).json.
 *
 * Also enforces an https-only scheme allowlist (http allowed only for
 * localhost so local dev works) to block dangerous URL schemes.
 */
function isCallbackOriginAllowed(callbackUrl, allowedOrigins) {
  if (!Array.isArray(allowedOrigins) || allowedOrigins.length === 0) {
    return false;
  }
  let parsed;
  try {
    parsed = new URL(callbackUrl);
  } catch {
    return false;
  }
  const isLocalhost = LOCAL_HOSTNAMES.has(parsed.hostname);
  const schemeOk = ALLOWED_CALLBACK_SCHEMES.has(parsed.protocol)
    || (isLocalhost && parsed.protocol === 'http:');
  if (!schemeOk) {
    return false;
  }
  return allowedOrigins.includes(parsed.origin);
}

// Per UAS contract (CallbackController.java -> PhoneRegistryResult.valueOf):
// PRHS appends exactly one query parameter to the inbound uasCallbackUrl:
// ?result=<value> where value is case-sensitive SUCCESS|FAILED|SKIPPED.
// Inbound URL already contains UAS's ?state=<nonce>&flow=phone-registry which
// must be preserved untouched (URLSearchParams.set('result', ...) does not
// touch other params). PII (phoneNumber, requestId) does NOT belong here —
// UAS reads phone status server-side via the strict PRHS API.
//
// Note: an older design had a `redirectToCallback(url, result)` helper that
// did the navigation itself, with the confirmation page and its countdown
// hand-rolled in React state. The module owns both now — we hand it the
// callback URL via `onSuccessRedirectUrl` (see onInit) and it renders its own
// success page before navigating.
const CALLBACK_RESULT_SUCCESS = 'SUCCESS';
// eslint-disable-next-line no-unused-vars -- exposed for future paths (FAILED for non-recoverable error redirects, SKIPPED for if/when skip is product-enabled)
const CALLBACK_RESULT_FAILED = 'FAILED';
// eslint-disable-next-line no-unused-vars
const CALLBACK_RESULT_SKIPPED = 'SKIPPED';
// How long the confirmation page stays up before the module navigates. Long
// enough to read the number back and confirm it's the right one.
const CALLBACK_REDIRECT_DELAY_SECONDS = 8;
// Upper bound on the GET /v2/is-phone-required preflight so a stalled request
// can't block onInit (and the inline form) forever; on timeout we fail open.
const PREFLIGHT_TIMEOUT_MS = 5000;

// PRHS owns the resend backoff — it grows per attempt on their side — so the UI
// never invents a wait of its own. It counts down whatever /v1/register-phone
// reports (resendCooldownSeconds / resendAllowedAt) and refuses to send until
// that window elapses. The constant below only covers the gap before the first
// server answer arrives.
//
// One slot is all the cooldown array needs: PRHS decides how long each wait is,
// the index doesn't, so every slot would hold the same number anyway. The module
// clamps its tier lookup to the last entry, so a single slot serves every
// attempt. Paired with `resendLimit: null`, which is what keeps the module from
// ending the countdown at some attempt count of its own — see the option's note
// where it's passed.
// Stand-in until PRHS reports a real window. Matches the OTP's own 300s
// validity, deliberately erring long — a too-short guess re-enables Send Code
// while the server is still rejecting, which is the OTP_RESEND_TOO_SOON bug
// this replaces.
const FALLBACK_RESEND_COOLDOWN_SECONDS = 300;
const RESEND_TOO_SOON_CODE = 'OTP_RESEND_TOO_SOON';
const VERIFY_RETRY_LIMIT_CODE = 'OTP_RETRY_LIMIT_EXCEEDED';
// Help-doc links offered alongside an error, keyed by the PRHS error code that
// earns them. The module renders the entry as a "Learn more" action under the
// banner text — its own element, so the copy itself stays a plain string (the
// banner sets it via textContent and would print any markup verbatim).
//
// A ban is permanent and self-service cannot lift it, so it is the one refusal
// where the user's next move is reading policy rather than retrying.
const ERROR_LEARN_MORE_URLS = {
  PHONE_NUMBER_BANNED: 'https://nvidia.custhelp.com/app/answers/detail/a_id/5832',
};
// How often the phone step re-renders its "please wait" notice. The displayed
// value only changes once a second; the faster tick is so the notice appears
// promptly when the user lands on the step (via "Edit phone number") and so it
// comes back quickly after the module blanks the banner on keystroke.
const COOLDOWN_NOTICE_TICK_MS = 500;
// The element the module renders into. Shared so the JSX below and the two
// effects that reach for it by id can't drift apart in a rename.
const VERIFICATION_CONTAINER_ID = 'verification-container';
// Names the card's heading for its role="group", the same pairing the module
// uses for its own steps.
const NOTHING_TO_MANAGE_HEADING_ID = 'nothing-to-manage-heading';

/** Build the final URL we navigate to after success: <callback>&result=SUCCESS. */
function buildSuccessRedirectUrl(callbackUrl) {
  const url = new URL(callbackUrl);
  url.searchParams.set('result', CALLBACK_RESULT_SUCCESS);
  return url.toString();
}


/**
 * PRHS API URL for fetch(). In development, use a same-origin path so CRA's package.json
 * "proxy" forwards to the real API and avoids browser CORS (localhost → api host).
 * In production builds, use the configured absolute base URL.
 */
function getPrhsApiRequestUrl(baseUrl, apiPath) {
  const path = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
  if (process.env.NODE_ENV === 'development') {
    return path;
  }
  const trimmed = (baseUrl || '').replace(/\/$/, '');
  return trimmed ? `${trimmed}${path}` : path;
}

/**
 * Token-derived phone-requirement preflight.
 *
 * Calls PRHS `GET /v2/is-phone-required`, which derives the user from the
 * id_token bearer (no path/query param) and returns a requirement status:
 *   - "REQUIRED"  — the user must register a phone.
 *   - "OPTIONAL"  — registration is offered but not mandatory.
 *   - "NONE"      — no registration needed (e.g. a number is already on file).
 *
 * Returns the raw status string, or null when the check can't be made
 * (no token, network/HTTP error, unexpected body). Callers fail open: a null
 * status is treated as "show the registration flow" so a flaky preflight never
 * blocks a user who genuinely needs to register.
 *
 * NOTE: this only reports *whether* a number is required, not the number
 * itself. The user's existing number (for edit-mode prefill) comes from the
 * separate token-derived GET /v1/me/phone-number (see fetchMyPhoneNumber).
 */
async function fetchIsPhoneRequired(baseUrl, bearer) {
  // Bound the preflight so a stalled request can't hang onInit (and with it the
  // whole inline flow) indefinitely. On timeout the abort surfaces as a thrown
  // error → caught below → null → fail open and show the registration form.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PREFLIGHT_TIMEOUT_MS);
  try {
    const url = getPrhsApiRequestUrl(baseUrl, '/v2/is-phone-required');
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${bearer}` },
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.status ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Shared results for the lookup below; `present` carries the number itself. */
const PHONE_ABSENT = { kind: 'absent' };
const PHONE_UNKNOWN = { kind: 'unknown' };

/**
 * Token-derived "get my registered phone number" for edit-mode prefill.
 *
 * Calls PRHS `GET /v1/me/phone-number`, which derives the user from the id_token
 * bearer and returns their registered number in E.164 (e.g. "+14083682090").
 *   - 200 { phoneNumber } — on file        → { kind: 'present', phoneNumber }
 *   - 204                 — nothing there  → { kind: 'absent' }
 *   - anything else       — we can't tell  → { kind: 'unknown' }
 *
 * Absent and unknown are kept apart because the manage-only block in onInit
 * turns "nothing on file" into a dead end. Collapsing a 500, a timeout or a
 * malformed body into "absent" would hand that dead end to a valid manage
 * visitor, under copy telling them they have no number registered — which may
 * be untrue. So only a 204 counts as absent; everything else stays unknown and
 * falls through to the form. Bounded by the same preflight timeout as the
 * requirement check so a stalled request can't hang onInit, and an abort reads
 * as unknown rather than as a confirmed absence.
 */
async function fetchMyPhoneNumber(baseUrl, bearer) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PREFLIGHT_TIMEOUT_MS);
  try {
    const url = getPrhsApiRequestUrl(baseUrl, '/v1/me/phone-number');
    const res = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${bearer}` },
      signal: controller.signal,
    });
    if (res.status === 204) return PHONE_ABSENT;
    if (!res.ok) return PHONE_UNKNOWN;
    const body = await res.json();
    // A 200 that omits the number is not PRHS reporting "none" — 204 is how it
    // says that — so this is an unexpected body, not a confirmed absence.
    return typeof body?.phoneNumber === 'string' && body.phoneNumber
      ? { kind: 'present', phoneNumber: body.phoneNumber }
      : PHONE_UNKNOWN;
  } catch {
    return PHONE_UNKNOWN;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Maps raw fetch/network errors to user-friendly messages.
 * CORS blocks and DNS failures both surface as TypeError("Failed to fetch")
 * with no status code or response body available to JavaScript.
 *
 * Every engine words that TypeError differently, and only Chrome says "Failed
 * to fetch": Safari says "Load failed", older WebKit just "Type error", and
 * Firefox "NetworkError when attempting to fetch resource". Matching only the
 * Chrome and Firefox spellings meant a plain connectivity failure on Safari —
 * including the iOS standalone web app this page runs as — fell through to the
 * generic "unexpected error" instead of saying the server was unreachable.
 */
function getNetworkErrorMessage(intl, err) {
  if (err instanceof TypeError) {
    const msg = (err.message || '').toLowerCase();
    if (
      msg.includes('failed to fetch') ||
      msg.includes('networkerror') ||
      msg.includes('load failed') ||
      msg.includes('type error')
    ) {
      return intl.formatMessage({
        id: 'home.error.network.unreachable',
        defaultMessage:
          'Unable to reach the server. This may be a network or configuration issue. Please try again later.',
      });
    }
    return intl.formatMessage({
      id: 'home.error.network.unexpected',
      defaultMessage: 'An unexpected error occurred. Please try again.',
    });
  }
  return (
    err?.message ||
    intl.formatMessage({
      id: 'home.error.network.unexpected',
      defaultMessage: 'An unexpected error occurred. Please try again.',
    })
  );
}

/** Parse a response body that may or may not be JSON. Returns null when it isn't. */
function parseJsonBody(responseBody) {
  try {
    return typeof responseBody === 'string' ? JSON.parse(responseBody) : responseBody;
  } catch {
    return null;
  }
}

/** Assemble the E.164 number PRHS keys both registration and its resend backoff on. */
function toE164(countryCode, phoneNumber) {
  const digits = (phoneNumber || '').replace(/\D/g, '');
  const cc = (countryCode || '+1').toString().trim();
  return cc.startsWith('+') ? `${cc}${digits}` : `+${cc}${digits}`;
}

/**
 * Read the resend window out of a PRHS response body, as an absolute instant.
 *
 * Prefers `resendCooldownSeconds` over `resendAllowedAt`. The two are meant to
 * say the same thing, and the duration is the one worth trusting:
 *
 * - It is measured against our own clock. `resendAllowedAt` is an instant on
 *   PRHS's clock, so counting down to it subtracts their clock from ours and
 *   shows the user the difference. A client running fast reads the window as
 *   shorter than it is, re-enables Send Code early, and earns the
 *   OTP_RESEND_TOO_SOON refusal this gate exists to avoid.
 * - It is the field that states the policy. When the two disagree, the
 *   timestamp is the one carrying the surcharge: a stated 60s cooldown was
 *   counting down from 1:15, and 600s from 10:15, on a client whose clock was
 *   within a second of the server's.
 *
 * Reading the duration instead costs only the response's time in flight —
 * milliseconds, and in the direction of waiting slightly too long.
 *
 * Returns null when the body carries neither, which lets callers keep whatever
 * window they already had rather than clobbering it with a guess.
 */
function readResendWindow(json) {
  if (!json || typeof json !== 'object') return null;
  const seconds = Number(json.resendCooldownSeconds);
  if (Number.isFinite(seconds) && seconds > 0) return Date.now() + seconds * 1000;
  const allowedAtMs = Date.parse(json.resendAllowedAt ?? '');
  if (Number.isFinite(allowedAtMs)) return allowedAtMs;
  return null;
}

/** Whole seconds left before `allowedAtMs`; 0 once the window has elapsed. */
function secondsUntil(allowedAtMs) {
  if (!allowedAtMs) return 0;
  return Math.max(0, Math.ceil((allowedAtMs - Date.now()) / 1000));
}

/**
 * Seconds the user still has to wait before PRHS will send another code.
 *
 * Deliberately not keyed by phone number. PRHS applies the backoff to the
 * caller, not to the number typed into the field, so a wait started by one
 * number still applies after editing to another. Keying this by number let
 * "Edit phone number" plus a single changed digit clear the countdown and
 * re-enable Send Code, which then spent a request to get the same refusal
 * back and dropped the user where they started.
 */
function resendWaitFor(gate) {
  return secondsUntil(gate.allowedAtMs);
}

/**
 * Point both resend gates at `allowedAtMs`.
 *
 * Filling the cooldown array is what makes the visible countdown agree with the
 * server. The module reads `options.resendCooldowns[attempt]` at the moment it
 * starts the next countdown, and its resolveOptions keeps the array we passed
 * by reference, so writing the slots here swaps the module's built-in 60s first
 * tier for PRHS's actual window before that timer starts. Callers must do this
 * before returning from onSendCode, since the module starts the countdown as
 * soon as that promise resolves.
 */
function recordResendWindow(gateRef, cooldownsRef, allowedAtMs, subject) {
  gateRef.current = { allowedAtMs };
  const seconds = secondsUntil(allowedAtMs);
  if (seconds > 0) cooldownsRef.current.fill(seconds);
  persistResendWindow(subject, allowedAtMs);
}

/**
 * Forget the resend window, because the server just did.
 *
 * PRHS deletes its whole resend record — the window and the attempt count — when
 * a code verifies, so the ladder a user climbed to get here does not follow them
 * out. Keeping our copy past that point is what leaves the UI counting down a
 * wait that no longer exists: Send Code sits disabled behind a timer only a page
 * reload can clear, refusing a code the server would happily have sent.
 *
 * Erring in this direction is cheap. If the server disagrees it says so on the
 * next send, with its own remaining window, and that arrives as a neutral notice
 * carrying an accurate countdown — a corrected timer rather than a stuck one.
 */
function releaseResendWindow(gateRef, cooldownsRef) {
  gateRef.current = { allowedAtMs: 0 };
  cooldownsRef.current.fill(FALLBACK_RESEND_COOLDOWN_SECONDS);
  clearPersistedResendWindow();
}

/**
 * m:ss, or h:mm:ss once the wait reaches an hour — matching how the module
 * renders its own "Request new code in {time}".
 *
 * The hour arm is not hypothetical: PRHS escalates the resend cooldown per
 * attempt (1m, 10m, 1h, 24h at the time of writing), so the third wait a user
 * hits is an hour and the fourth is a day. Minutes-only formatting rendered
 * those as "60:00" and "1440:00", the second of which nobody reads as a day.
 */
function formatWaitTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * `Retry-After` in delta-seconds, the only form PRHS sends. Returns 0 when the
 * header is absent or unusable so callers fall back to wait-unknown copy rather
 * than starting a countdown from a number we don't have.
 */
function parseRetryAfterSeconds(headerValue) {
  const seconds = Number.parseInt(headerValue ?? '', 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
}

/**
 * A refusal the user has to sit out, as opposed to something they got wrong.
 *
 * `retryAfterSeconds` is the module's signal for exactly that distinction: it
 * states the wait in the banner's neutral info tone rather than as a red error,
 * leaves the phone field unmarked, holds the resend button shut for precisely
 * that long, and spends none of the resend ladder to do it. Without the field
 * the same rejection reads as a failure and re-enables the button, inviting a
 * press PRHS has already refused.
 *
 * Set as a plain property because the module reads it duck-typed, which saves
 * importing its error class into a file the tests mock the module out of.
 *
 * Omitted when the wait is zero or unknown — the message still explains itself,
 * and claiming a window we can't name would start a countdown that never moves.
 */
function cooldownError(message, retryAfterSeconds) {
  const err = new Error(message);
  if (retryAfterSeconds > 0) {
    err.retryAfterSeconds = retryAfterSeconds;
  }
  return err;
}

/**
 * Rejection for an API error, carrying the code's help-doc link when it has one.
 *
 * `learnMoreUrl` is set as a plain property for the same reason as the cooldown
 * field above: the module reads it duck-typed, so this file never has to import
 * its error class.
 */
function httpError(message, errorCode) {
  const err = new Error(message);
  const learnMoreUrl = ERROR_LEARN_MORE_URLS[errorCode];
  if (learnMoreUrl) {
    err.learnMoreUrl = learnMoreUrl;
  }
  return err;
}

// A ban is the same verdict on both steps and reads the same to the user, so the
// two maps below share one descriptor rather than drifting apart. APS enforces it
// at the write, which is why it can still arrive at verify time — see the note on
// VERIFY_OTP_ERRORS.
const PHONE_BANNED_ERROR = {
  id: 'home.error.phoneBanned',
  defaultMessage:
    'This phone number has been permanently disabled due to a violation. If you believe this is an error, please contact NVIDIA Customer Care for assistance, or try a different phone number.',
};

// Maps of HTTP status + API error code to a localizable descriptor
// ({ id, defaultMessage }) resolved via intl.formatMessage in getHttpErrorMessage.
const SEND_CODE_ERRORS = {
  400: {
    PHONE_NUMBER_INVALID: { id: 'home.error.sendCode.phoneInvalid', defaultMessage: 'The phone number you entered is invalid. Please check and try again.' },
    // Read off APS's usage record (a 200 carrying `banned: true`) before any SMS
    // is sent, so this is the step where a banned number normally lands. It
    // outranks both unchanged and already-registered on the PRHS side, so no
    // other 400 code can mask it.
    PHONE_NUMBER_BANNED: PHONE_BANNED_ERROR,
  },
  401: { _default: { id: 'home.error.sessionExpired', defaultMessage: 'Your session has expired. Please refresh the page and try again.' } },
  409: {
    // Registered to a *different* account (with no allowlist quota left).
    PHONE_NUMBER_ALREADY_REGISTERED: { id: 'home.error.sendCode.alreadyRegisteredSelf', defaultMessage: 'The number you provided is already registered to an existing account. Log in with that account instead, or try again.' },
    // The number the user submitted is the one they already have on file (a
    // no-op update). PRHS returns a distinct code so we can tell the user it's
    // their own number rather than a collision with another account.
    PHONE_NUMBER_UNCHANGED: { id: 'home.error.sendCode.unchanged', defaultMessage: 'This is your current phone number' },
  },
  // The SMS provider refused this specific number (fraud block, blocked country,
  // or a landline). Retrying it cannot succeed, so the copy sends the user to a
  // different number instead of asking them to wait like the 5xx copy below.
  //
  // All three causes share this one error code, so one message has to serve them
  // all. Naming landlines gives the common case something to act on, and the
  // support fallback is the only exit for a blocked country, where no number the
  // user owns will work and "try another number" alone is an infinite loop.
  422: {
    OTP_DELIVERY_BLOCKED: { id: 'home.error.sendCode.deliveryBlocked', defaultMessage: "We couldn't send a verification code to that number. Landlines and numbers in some countries can't receive codes. Please try a different mobile number, or contact support if none work." },
  },
  // The _default covers a 429 we didn't originate: an edge/WAF rate limit never
  // reaches the API (so it carries no errorCode, and nothing about it appears in
  // the service logs). Without it such a response fell through to the bare
  // "Request failed (429)".
  429: {
    PHONE_REPUTATION_NOT_MET: { id: 'home.error.sendCode.reputation', defaultMessage: "The number you have provided doesn't meet our security requirements. Try again with a different number that's associated with a mobile carrier." },
    _default: { id: 'home.error.sendCode.resendTooSoonUnknownWait', defaultMessage: 'You recently requested a code. Please wait a moment before requesting another one.' },
  },
  // 502 is a failed upstream, 503 is PRHS unable to serve at all. Neither is the
  // user's to fix, so both get the same wait-and-retry copy.
  502: { _default: { id: 'home.error.sendCode.serviceUnavailable', defaultMessage: 'Our verification service is temporarily unavailable. Please try again in a few minutes.' } },
  503: { _default: { id: 'home.error.sendCode.serviceUnavailable', defaultMessage: 'Our verification service is temporarily unavailable. Please try again in a few minutes.' } },
};

const VERIFY_OTP_ERRORS = {
  400: {
    OTP_NOT_MATCH: { id: 'home.error.verify.otpIncorrect', defaultMessage: 'The code you entered is incorrect. Please try again.' },
    // Not redundant with the send step's check: that one is a read of APS's usage
    // record, while this one is APS refusing the write (403). The ban can land in
    // the minutes the user spends typing the code, and a write-time 403 also
    // covers a banned *user*, which no lookup keyed on the phone hash can see.
    PHONE_NUMBER_BANNED: PHONE_BANNED_ERROR,
    _default: { id: 'home.error.verify.invalidRequest', defaultMessage: 'Invalid request. Please try again.' },
  },
  401: { _default: { id: 'home.error.sessionExpired', defaultMessage: 'Your session has expired. Please refresh the page and try again.' } },
  409: { PHONE_NUMBER_ALREADY_REGISTERED: { id: 'home.error.verify.alreadyRegisteredOther', defaultMessage: 'The number you provided is already registered to an existing account. Log in with that account instead, or try again.' } },
  422: {
    OTP_NOT_MATCH: { id: 'home.error.verify.otpIncorrect', defaultMessage: 'The code you entered is incorrect. Please try again.' },
    OTP_EXPIRED: { id: 'home.error.verify.otpExpired', defaultMessage: 'Your verification code has expired. Please request a new one.' },
    _default: { id: 'home.error.verify.failedRequestNew', defaultMessage: 'Verification failed. Please request a new code and try again.' },
  },
  // Only edge/WAF 429s reach this map: the PRHS lockout carries our own code and
  // is answered in onVerifyCode before getHttpErrorMessage is consulted.
  429: { _default: { id: 'home.error.sendCode.serviceUnavailable', defaultMessage: 'Our verification service is temporarily unavailable. Please try again in a few minutes.' } },
  500: {
    PHONE_NUMBER_REGISTRATION_FAILED: { id: 'home.error.verify.registrationFailed', defaultMessage: 'Phone registration failed due to a server error. Please try again later.' },
    _default: { id: 'home.error.verify.internal', defaultMessage: 'An internal error occurred. Please try again later.' },
  },
  // Same copy as the phone step: the OTP was not consumed, so the code the user
  // holds is still good and "try again" beats "request a new code".
  502: { _default: { id: 'home.error.sendCode.serviceUnavailable', defaultMessage: 'Our verification service is temporarily unavailable. Please try again in a few minutes.' } },
  503: { _default: { id: 'home.error.sendCode.serviceUnavailable', defaultMessage: 'Our verification service is temporarily unavailable. Please try again in a few minutes.' } },
};

/**
 * Extracts a user-friendly message from an HTTP error response.
 * Checks the status code + error code from the JSON body against the provided
 * error map (descriptors localized via intl), then falls back to the body's
 * message field (server-provided, not translatable), then a generic message.
 */
function getHttpErrorMessage(intl, status, responseBody, errorMap) {
  const json = parseJsonBody(responseBody);

  const errorCode = json?.errorCode || json?.error_code || json?.code;
  const statusMessages = errorMap[status];
  if (statusMessages) {
    const mapped = (errorCode && statusMessages[errorCode]) || statusMessages._default;
    if (mapped) return intl.formatMessage(mapped);
  }

  const bodyMessage =
    json?.message ??
    json?.errorMessage ??
    json?.detail ??
    (typeof json?.error === 'string' ? json.error : json?.error?.message);
  if (bodyMessage) return bodyMessage;

  // Only ever surface a body we could NOT parse as JSON (a proxy's plain-text
  // or HTML error page). Echoing a parsed-but-unmapped body would put raw
  // wire format on screen, e.g. PRHS's own {"errorCode":"..."} for any code
  // missing from the map above.
  if (!json && typeof responseBody === 'string' && responseBody.trim()) {
    return responseBody.slice(0, 200);
  }

  return intl.formatMessage(
    { id: 'home.error.requestFailed', defaultMessage: 'Request failed ({status})' },
    { status }
  );
}

// NOTE: onInit runs two token-derived PRHS calls before showing the form:
//   1. GET /v2/is-phone-required (fetchIsPhoneRequired) — when the user doesn't
//      need to register (status NONE), we bounce straight back to the UAS
//      callback instead of showing the form.
//   2. GET /v1/me/phone-number (fetchMyPhoneNumber) — when a number is on file,
//      we return it as `initialPhoneNumber` so the module opens in edit mode.
//
// Skip is intentionally NOT offered at this stage (product decision): the flow
// is binary — the user either sees the registration page or doesn't. They never
// see it and skip it. So `getSkipConfig`/`onSkip` are deliberately omitted (the
// module hides the Skip button when getSkipConfig is absent) and PRHS's skip
// action (POST /v1/skip-phone-registry) is left unused. Both REQUIRED and
// OPTIONAL show the form; only NONE bounces back without it.

/** US and Canada stay at the top of the dropdown, as PRHS itself orders them. */
const PRIORITY_COUNTRIES = ['US', 'CA'];

/**
 * Pick the phone-input default country from the active UI locale so the
 * verification module follows the page's language dropdown. The locale's
 * region subtag (e.g. "tr-TR" -> "TR") names the country; falls back to the
 * United States when there's no match (e.g. region-less locales like "en").
 *
 * The module matches `defaultCountryCode` by country NAME, so this has to
 * produce names the same way the list does. It used to read them off the
 * module's bundled COUNTRY_CODES, which worked only while the list came from
 * there too — against the PRHS list, whose names come from Intl.DisplayNames in
 * the active locale, a bundled English name matches nothing and the default
 * silently becomes whatever sorts first.
 */
function defaultCountryForLocale(locale) {
  const region = (locale || '').split('-')[1]?.toUpperCase() || 'US';
  return regionDisplayName(region, locale) || 'United States';
}

function Home() {
  const intl = useIntl();
  // The verification module is constructed once and its event handlers live for
  // the lifetime of that instance. Keep the latest intl in a ref so those
  // handlers always format error copy in the current language, while the module
  // itself follows the language dropdown via setIntl() (see the effects below) —
  // no teardown/recreate, so in-progress phone/OTP entry is preserved.
  const intlRef = useRef(intl);
  intlRef.current = intl;
  // Read the id_token once per render and derive every auth value from it, so
  // they stay consistent with the live token the modal-mount guard reads below.
  // readStoredStarfleetIdToken() self-cleans expired tokens; deriving from a
  // single per-render read (instead of a location-keyed memo) means a token that
  // expires/clears without a route change can't leave isAuthenticated stale-true
  // while the guard blocks the modal — which would render an empty verification
  // area instead of the sign-in prompt. The OAuth callback navigates to '/',
  // which mounts Home fresh, so this re-evaluates with the new token then too.
  //
  // UAS launches include login_hint=email. Drop a leftover sessionStorage token
  // from a prior account (DMO logout does not clear PRHS tab storage) before we
  // treat the visitor as signed in — otherwise /v2/is-phone-required runs as
  // the wrong user while OAuth bootstrap is still pending.
  const launchHints = getStarfleetLoginHintsFromQueryString();
  if (launchHints?.loginHint) {
    clearStoredStarfleetIdTokenIfLoginHintMismatch(launchHints.loginHint);
  }
  const starfleetIdToken = readStoredStarfleetIdToken();
  const signedInUser = useMemo(
    () =>
      starfleetIdToken
        ? getStarfleetAuthDisplayProfile(
            starfleetIdToken,
            // Written at the OAuth callback, and only when the token itself
            // named nobody. Read here rather than stored in state because it is
            // cleared alongside the token it describes.
            readStoredStarfleetUserInfo()
          )
        : null,
    [starfleetIdToken]
  );

  // Every PRHS call needs a Starfleet id_token bearer; without one the phone form
  // can do nothing (Send Code would only trigger a re-auth reload). So treat "no
  // token" as "not signed in" and surface a sign-in prompt instead of the form.
  // The bundled Starfleet OAuth effect below still redirects to login when it can
  // resolve hints (the normal UAS-launched flow); this guard covers the cases
  // where it can't — direct visits, a cancelled login, or missing hints — so we
  // never present a form the user can't actually submit.
  const isAuthenticated = Boolean(starfleetIdToken);

  const modalRef = useRef(null);
  const appConfigRef = useRef(null); // full config from loadPublicAppConfig() for prhsApiUrl etc.
  // Raw PRHS country entries, kept so a locale switch can re-localize their
  // names without refetching. Null until getCountryList runs, and stays null if
  // the list is unavailable — in which case the module is on its bundled list
  // and there is nothing here to re-localize.
  const partnerCountriesRef = useRef(null);
  const phoneForVerifyRef = useRef(null); // full phone (e.g. +14083682090) set on Send Code success, used in Verify
  // Server-authoritative resend gate: the instant PRHS will accept another
  // code request. Account-wide, not per number — see resendWaitFor.
  const resendGateRef = useRef({ allowedAtMs: 0 });
  // Handed to the module by reference as its `resendCooldowns` option and then
  // mutated in place as PRHS reports each new window — see recordResendWindow.
  const resendCooldownsRef = useRef([FALLBACK_RESEND_COOLDOWN_SECONDS]);
  // The cooldown text we last pushed into the module's error banner, or null
  // when we aren't showing one. Lets the ticker below tell its own message
  // apart from a real error (an invalid number, a failed send) so it only ever
  // clears the banner it raised itself.
  const cooldownNoticeRef = useRef(null);
  // Why the last send was refused, when that refusal also armed the gate — the
  // provider turning down the number rather than a plain wait. The notice shows
  // it ahead of the countdown: alone, the countdown tells someone whose number
  // was refused to wait for something no wait can fix.
  const sendVerdictRef = useRef(null);
  // Same idea for the "not a valid number" text the country-change watcher
  // below raises, so it also only ever clears its own banner.
  const invalidNumberNoticeRef = useRef(null);
  const oauthRedirectIssuedRef = useRef(false);
  // Last status from the GET /v2/is-phone-required preflight (REQUIRED/OPTIONAL/NONE/null).
  const phoneRequirementRef = useRef(null);
  // This page embeds the verification module as persistent page content, not a
  // dismissible overlay. The module closes itself on backdrop-click and Escape;
  // we suppress those so an accidental click/Escape can't make the form vanish.
  // Only the success flow (which flips this to true in onSuccess, just before
  // the module calls close()) is allowed to actually close the module.
  const allowModalCloseRef = useRef(false);
  const callbackUrlRef = useRef(null); // captured UAS callback URL from ?uasCallbackUrl= or sessionStorage; validated lazily inside the modal's onInit
  // Whether the auth bootstrap has decided that the visitor will NOT be silently
  // signed in (no token, and the bundled-hints OAuth redirect won't fire). Until
  // that's known we show a neutral loading state instead of the "Sign in
  // required" prompt — otherwise an unauthenticated visitor who is about to be
  // redirected to Starfleet briefly sees a misleading "not signed in" flash.
  const [signInResolved, setSignInResolved] = useState(false);
  // The hints the bootstrap resolved (query, persisted, or bundled file). Kept so
  // the fallback screen's retry can redirect with the same identity rather than
  // re-deriving it — and so we can tell whether a retry is possible at all.
  const resolvedHintsRef = useRef(null);
  // True once we've redirected from the retry button: keeps the button disabled
  // while the browser is on its way to Starfleet.
  const [signingInAgain, setSigningInAgain] = useState(false);
  // After OAuth, Layout + #verification-container mount before PhoneVerificationModal
  // finishes onInit (config + is-phone-required + me/phone-number). Hold the same
  // spinner as an overlay until open() resolves so users don't see empty chrome.
  const [verificationReady, setVerificationReady] = useState(false);
  // Set when onInit decides to bounce back to UAS (status NONE). Keep the overlay
  // up until the browser navigates away instead of briefly revealing the form.
  const redirectingAwayRef = useRef(false);
  // Set when onInit finds a manage-only entry with no number on file. A ref
  // rather than state because onInit runs inside open(), and re-rendering from
  // there would pull the container out from under the module while it is still
  // writing into it; the state flip happens once open() has resolved.
  const nothingToManageRef = useRef(false);
  const [nothingToManage, setNothingToManage] = useState(false);
  // Set when Close ran and the tab is still here, so the screen can say so
  // rather than leave a button that visibly did nothing.
  const [tabCloseRefused, setTabCloseRefused] = useState(false);

  // Capture ?uasCallbackUrl= from URL or sessionStorage on mount; persist so the Starfleet
  // OAuth full-page roundtrip does not lose it. Allowlist + scheme validation happens
  // inside the modal's onInit hook (which loads app config and decides whether to enable
  // the modal's native onSuccessRedirectUrl behavior).
  useEffect(() => {
    const fromQuery = getCallbackUrlFromQuery();
    const fromStorage = restoreCallbackUrl();
    const callbackUrl = fromQuery || fromStorage;
    if (callbackUrl) {
      callbackUrlRef.current = callbackUrl;
      if (fromQuery) persistCallbackUrl(fromQuery);
    }
  }, []);

  /** Optional Starfleet OAuth: load config, then redirect to /authorize when bundled hints + client are configured. */
  useEffect(() => {
    if (oauthRedirectIssuedRef.current) return;
    let cancelled = false;

    async function maybeStartBundledStarfleetOAuth() {
      if (typeof window === 'undefined') return;
      if (oauthRedirectIssuedRef.current) return;

      const cfg = await loadPublicAppConfig();
      if (cancelled) return;
      // Config failed to load → can't silently sign in; fall back to the prompt.
      if (!cfg) {
        setSignInResolved(true);
        return;
      }
      appConfigRef.current = cfg;

      // UAS handoff carries login_hint. If sessionStorage still has a prior
      // account's id_token, clear it so silent OAuth runs again with the
      // launched user's login_hint (DMO has already switched the NVIDIA
      // session; we do not use prompt=login).
      const hintsFromQuery = getStarfleetLoginHintsFromQueryString();
      if (hintsFromQuery?.loginHint) {
        clearStoredStarfleetIdTokenIfLoginHintMismatch(hintsFromQuery.loginHint);
      }

      // Already authenticated (and matches login_hint when present) → form renders.
      if (readStoredStarfleetIdToken()) return;
      // Silent login disabled by config → show the prompt.
      if (!cfg.useBundledLoginHintsForStarfleet) {
        setSignInResolved(true);
        return;
      }

      const { clientId, loginOrigin } = resolveStarfleetOAuthSettings(cfg);
      // OAuth not configured → can't redirect; show the prompt.
      if (!clientId || !loginOrigin) {
        setSignInResolved(true);
        return;
      }

      const appName = (cfg.starfleetApplicationName || 'PRHS').trim();
      // Order matters. The query string is the authoritative launch handoff, so
      // it wins. Failing that, hints we stored on a previous load are what make
      // re-auth possible at all: the callback navigates to '/' with replace, so
      // the query is long gone by the time a token expires mid-session, and
      // login-hints.json is an empty array outside local dev. Without the
      // persisted copy this resolves to null and a routine expiry becomes a
      // dead-end "Sign in required".
      const hints =
        hintsFromQuery ||
        restoreStarfleetLoginHints() ||
        (await fetchLoginHintsForApplication(appName));
      if (cancelled) return;
      // No login hints → we won't redirect; show the prompt.
      if (!hints) {
        setSignInResolved(true);
        return;
      }
      resolvedHintsRef.current = hints;
      // Survive the roundtrip so the next expiry can recover silently.
      persistStarfleetLoginHints(hints);

      // Cross-reload loop guard. Each redirect that comes back without a token
      // burns one attempt; a successful exchange clears the budget. Spent means
      // something is wrong that redirecting again won't fix, so stop and let the
      // fallback screen offer a manual retry.
      if (readReauthAttempts() >= MAX_SILENT_REAUTH_ATTEMPTS) {
        setSignInResolved(true);
        return;
      }

      if (oauthRedirectIssuedRef.current) return;
      oauthRedirectIssuedRef.current = true;

      recordReauthAttempt();
      await startStarfleetAuthorize({ cfg, clientId, loginOrigin, hints });
    }

    // If the bootstrap throws before it can either redirect or decide (e.g. a
    // login-hints fetch rejects), fall back to the sign-in prompt instead of
    // leaving the full-screen spinner up forever.
    maybeStartBundledStarfleetOAuth().catch(() => {
      if (!cancelled) setSignInResolved(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Don't instantiate the verification flow for an unauthenticated visitor —
    // the render path below shows a sign-in prompt instead of the form.
    if (!getStarfleetBearerToken()) {
      setVerificationReady(false);
      redirectingAwayRef.current = false;
      return undefined;
    }
    if (modalRef.current) return undefined;
    if (typeof document === 'undefined') return undefined;
    const container = document.getElementById(VERIFICATION_CONTAINER_ID);
    if (!container) return undefined;

    let cancelled = false;
    redirectingAwayRef.current = false;
    setVerificationReady(false);

    const modal = new PhoneVerificationModal({
          container,
          // Embed the flow directly in the page (no overlay/backdrop/scroll-lock/
          // focus-trap, and no Escape/backdrop dismissal). This page hosts the
          // verification as persistent page content, so inline mode is the natural
          // fit — the surrounding page stays interactive and the form can't be
          // accidentally dismissed.
          mode: 'inline',
          // Required by the localized module: every user-facing string is resolved
          // via intl.formatMessage({ id: 'phoneVerification.*', defaultMessage }).
          // We pass the app's react-intl instance so the modal follows the page's
          // language dropdown; override copy by adding phoneVerification.* keys to
          // src/lang/<locale>.json (missing keys fall back to the English default).
          intl: intlRef.current,
          // Country name (not dial code) — the new module disambiguates shared dial codes (e.g. US/CA both +1).
          // Derived from the locale at construction time. We deliberately don't
          // change it on later locale switches: setIntl() preserves the user's
          // already-selected country, which is the desired behavior.
          defaultCountryCode: defaultCountryForLocale(intlRef.current.locale),
          // The real country list. Without this the module serves its bundled
          // 47, which is not what this partner supports; PRHS derives ~200 from
          // GES internationalization data. Resolved after onInit, so the config
          // it needs for the base URL is already loaded.
          //
          // Returning [] on failure is the fail-open path: the module reads it
          // as "no list" and falls back to its bundle, which is wrong but usable,
          // rather than leaving the user with no country to pick.
          getCountryList: async () => {
            const baseUrl =
              appConfigRef.current?.prhsApiUrl ?? process.env.REACT_APP_PRHS_API_URL;
            // getPrhsApiRequestUrl falls back to a same-origin path, which is
            // deliberate in development so CRA's proxy can forward it. In a
            // production build that fallback would put the id_token in an
            // Authorization header aimed at the app host, which has no use for
            // it and would only log it, so an unconfigured base URL means don't
            // ask. Same shape as the onInit preflights below. A missing bearer
            // needs no guard: loadPartnerCountries answers null, which maps to
            // the empty list.
            if (!baseUrl && process.env.NODE_ENV !== 'development') return [];
            const entries = await loadPartnerCountries(
              getPrhsApiRequestUrl(baseUrl, '/v1/partners/countries'),
              getStarfleetBearerToken()
            );
            const countries = toModuleCountryList(entries, intlRef.current.locale);
            // Kept raw so the locale effect below can re-localize the names
            // without another request — but only when the module actually took
            // this list. An empty result leaves it on its bundled 47, and
            // re-localizing a list it never adopted would just be work with
            // nowhere to land.
            partnerCountriesRef.current = countries.length > 0 ? entries : null;
            return countries;
          },
          priorityCountries: PRIORITY_COUNTRIES,
          // Passed by reference and rewritten in place from each PRHS response
          // (recordResendWindow), so "Request new code in {time}" counts down
          // the server's real backoff. The module's default first tier is 60s,
          // which expires long before PRHS will accept a resend and was letting
          // users click straight into OTP_RESEND_TOO_SOON.
          resendCooldowns: resendCooldownsRef.current,
          // No client-side cap. The module's default is to stop counting down
          // and show "you've reached the maximum number of attempts, please try
          // again tomorrow" once the resend count reaches resendCooldowns.length
          // — a cap and a reset time that no PRHS response ever states, and
          // being client-side it cleared on refresh anyway. PRHS escalates to a
          // day-long wait and reports it in resendCooldownSeconds like any other
          // window, so the honest thing is to keep counting that down: the user
          // can see exactly when they may retry.
          resendLimit: null,
          // 0 turns off the module's code-expiry banner and its countdown. BORG
          // owns the OTP lifetime (10 minutes, and its own SMS says so), and it
          // never reports that window to us, so the module's 300s default was a
          // guess that ran out while the code was still good. Because the timer
          // also disabled Verify when it fired, a user who took longer than five
          // minutes held a code PRHS would still accept and had no way to submit
          // it — and after the last resend tier, no resend button either. Expiry
          // is now surfaced only when it actually happens: PRHS answers a stale
          // code with 422 OTP_EXPIRED and VERIFY_CODE_ERRORS turns that into
          // "Your verification code has expired. Please request a new one."
          codeExpiry: 0,
          // onInit runs once per open(), awaited. Loads app config (so onSendCode/onVerifyCode
          // have prhsApiUrl), validates the captured UAS callback URL against
          // allowedCallbackOrigins, and returns onSuccessRedirectUrl override when valid so
          // the modal navigates to <uasCallbackUrl>?result=SUCCESS natively after a successful
          // verify. When the URL is missing or disallowed, the modal closes after success and
          // our onSuccess hook drives the inline standalone confirmation instead.
          onInit: async () => {
            const cfg = await loadPublicAppConfig();
            if (cfg) appConfigRef.current = cfg;

            // Token-derived requirement preflight. If PRHS reports this user no
            // longer needs to register (status NONE — e.g. a number is already on
            // file), don't make them do it again: when we have a validated UAS
            // callback, bounce straight back as success. Without an API URL or
            // bearer (local dev) we skip the check and show the flow as usual.
            const preflightBaseUrl =
              cfg?.prhsApiUrl ?? process.env.REACT_APP_PRHS_API_URL;
            const preflightBearer = getStarfleetBearerToken();

            // Has to land before the form is interactive, or the first click
            // goes out against a gate we haven't restored yet.
            const preflightSubject = getStarfleetIdTokenSubject(preflightBearer);
            const storedUntil = restoreResendWindow(preflightSubject);
            if (storedUntil) {
              recordResendWindow(
                resendGateRef,
                resendCooldownsRef,
                storedUntil,
                preflightSubject
              );
            }

            // The requirement check (GET /v2/is-phone-required) and the edit-mode
            // prefill lookup (GET /v1/me/phone-number) are independent and were
            // previously awaited one after the other (~0.5s + ~0.2s serial). Fire
            // them together so the common form-showing path finishes in the slower
            // of the two, not their sum. Both fail open to null and neither throws
            // (each catches internally), so the unawaited one is safe to leave in
            // flight. We still await the requirement check first and, on NONE with
            // a callback, redirect WITHOUT awaiting the prefill — that path leaves
            // the page, so blocking on a number we'll never use would only slow it.
            let statusPromise = null;
            let existingPromise = null;
            if (preflightBaseUrl && preflightBearer) {
              statusPromise = fetchIsPhoneRequired(preflightBaseUrl, preflightBearer);
              existingPromise = fetchMyPhoneNumber(preflightBaseUrl, preflightBearer);
            }

            let status = null;
            if (statusPromise) {
              status = await statusPromise;
              phoneRequirementRef.current = status;
              if (status === 'NONE') {
                const cb = callbackUrlRef.current;
                if (cb && isCallbackOriginAllowed(cb, cfg?.allowedCallbackOrigins)) {
                  // Already satisfied → return the user to UAS as success without
                  // showing the form. (No "registered" confirmation: they didn't
                  // just register here.) Keep the bootstrap spinner up until the
                  // browser leaves — otherwise open() would reveal an empty form.
                  redirectingAwayRef.current = true;
                  clearCallbackUrl();
                  if (typeof window !== 'undefined') {
                    window.location.assign(buildSuccessRedirectUrl(cb));
                  }
                  return undefined;
                }
                // No safe callback to bounce to (e.g. standalone/dev) — fall
                // through and let the user register anyway.
              }
            }

            // Edit-mode prefill: when PRHS has a number on file for this user
            // (GET /v1/me/phone-number, fired above in parallel), seed the form
            // with it so they land in edit mode (the module shows
            // editTitle/editDescription and a filled, submittable field).
            // Anything but a number → normal empty form.
            let initOverrides;
            const phoneOnFile = existingPromise
              ? await existingPromise
              : // No bearer, so nothing was asked and nothing is known. Not the
                // same as PRHS reporting no number, which is why this can't be
                // allowed to reach the block below.
                PHONE_UNKNOWN;

            // A manage-only entry with nothing to manage.
            //
            // The missing UAS callback is what identifies the visit: a GeForce
            // NOW registration always carries one, so its absence means the
            // standalone "manage your phone number" surface. With no number on
            // file that surface has nothing to act on, and the empty form we
            // would otherwise show is a working registration — letting a number
            // be added through a door that exists only to change one.
            //
            // Only a confirmed absence (204) qualifies. A failed or stalled
            // lookup is 'unknown' and falls through to the form: this is a dead
            // end, so getting here wrongly would strand someone who does have a
            // number, under copy saying they don't.
            //
            // A stop rather than a redirect because there is nowhere to send
            // them: having no callback is the condition that got us here.
            if (
              phoneOnFile.kind === 'absent' &&
              !callbackUrlRef.current &&
              blocksManageEntryWithoutPhone(cfg)
            ) {
              nothingToManageRef.current = true;
              return undefined;
            }

            if (phoneOnFile.kind === 'present') {
              initOverrides = { initialPhoneNumber: phoneOnFile.phoneNumber };
            }

            const callbackUrl = callbackUrlRef.current;
            // Jarvis / standalone (no UAS callback): let the module show its own
            // success page and STAY on it — no redirect, no countdown. Omitting
            // onSuccessRedirectUrl is the signal for "stay" (see module docs).
            if (!callbackUrl) return initOverrides;
            if (!isCallbackOriginAllowed(callbackUrl, cfg?.allowedCallbackOrigins)) {
              console.warn(
                '[Home] Callback URL origin not in allowedCallbackOrigins, ignoring:',
                callbackUrl
              );
              callbackUrlRef.current = null;
              clearCallbackUrl();
              return initOverrides;
            }
            // UAS: validated callback → the module shows its success page with a
            // live "Redirecting in Ns…" countdown, then navigates to
            // <callback>&result=SUCCESS. The module now owns the post-success
            // redirect (onSuccessRedirectUrl + successRedirectDelaySeconds > 0),
            // so we no longer hand-roll the confirmation/countdown ourselves.
            return {
              ...initOverrides,
              onSuccessRedirectUrl: buildSuccessRedirectUrl(callbackUrl),
              successRedirectDelaySeconds: CALLBACK_REDIRECT_DELAY_SECONDS,
            };
          },
          // Format validation deliberately does NOT happen here. The module's
          // built-in validator returns false for malformed numbers and surfaces a
          // hardcoded "Enter a valid number" message, so we re-run the SAME
          // libphonenumber-js check (exported as isValidPhoneNumber) inside
          // onSendCode instead and throw a more helpful message, which the module
          // renders in its errorBanner. Drop that split if/when the module exposes
          // configurable error messages.
          //
          // What this hook is used for is the resend gate: it's the only control
          // the module gives us over whether the phone-step Send Code button is
          // enabled, so returning false while PRHS is still counting down keeps
          // that button inert. Without it, "Edit phone number" tears down the
          // verify-step timer and lands on a step that would happily re-submit
          // with no wait at all. Enter is covered too — the module checks the
          // same disabled flag before acting on it.
          //
          // The arguments are ignored on purpose: the wait belongs to the
          // caller rather than to the number in the field, so editing the
          // number doesn't buy a fresh one.
          validatePhoneNumber: () => resendWaitFor(resendGateRef.current) === 0,
          // Skip is hidden by default in the new module when getSkipConfig is omitted; product requires hidden.
          onSendCode: async ({ countryCode, phoneNumber }) => {
            // Read the live intl (the modal may have switched language since it
            // was constructed) so any error copy is in the current language.
            const intl = intlRef.current;
            // Modal manages button loading state and step transitions automatically.
            // Throw on errors → modal returns to phone step with the message displayed.
            // Same handler is invoked for both initial Send Code and Resend.
            // SendCodeData payload only carries countryCode + phoneNumber; the country
            // NAME (which isValidPhoneNumber needs to resolve to a region code) lives on
            // modal.state.country and is set whenever the user selects a flag.
            const selectedCountry = modalRef.current?.state?.country;
            if (!isValidPhoneNumber(selectedCountry, countryCode, phoneNumber)) {
              throw new Error(
                intl.formatMessage({
                  id: 'home.error.invalidPhoneClient',
                  defaultMessage:
                    'This is not a valid phone number. Please check the country code and number format.',
                })
              );
            }
            const baseUrl = appConfigRef.current?.prhsApiUrl ?? process.env.REACT_APP_PRHS_API_URL;
            const starfleetBearer = getStarfleetBearerToken();
            // Bound to the token this send authenticates with, not re-read after
            // it: readStoredStarfleetIdToken drops an expired token and answers
            // null, so a later read can file the cooldown under the wrong
            // account or, with no subject at all, not persist it.
            const sendSubject = getStarfleetIdTokenSubject(starfleetBearer);
            const phoneNumberDigits = (phoneNumber || '').replace(/\D/g, '');
            const cc = (countryCode || '+1').toString().trim();
            const e164 = toE164(cc, phoneNumberDigits);
            phoneForVerifyRef.current = e164;
            // Last line of defence for the resend backoff. The countdown and the
            // disabled Send Code button should both have stopped the user before
            // here, so reaching this means one of them was bypassed (a stale
            // timer, a step transition that reset it, a double submit). Fail
            // locally with the time still to wait rather than spending a request
            // on a guaranteed OTP_RESEND_TOO_SOON.
            const waitSeconds = resendWaitFor(resendGateRef.current);
            if (waitSeconds > 0) {
              throw cooldownError(
                intl.formatMessage(
                  {
                    id: 'home.error.sendCode.resendTooSoon',
                    defaultMessage:
                      'Please wait {time} before requesting another code.',
                  },
                  { time: formatWaitTime(waitSeconds) }
                ),
                waitSeconds
              );
            }
            // This attempt supersedes the last refusal, so its verdict is spent;
            // the error path below sets a fresh one if this send is refused too.
            sendVerdictRef.current = null;
            if (!baseUrl) {
              // Mock for local dev only (no PRHS API URL configured). Still arm
              // the gate so the cooldown behaves the same way it will in prod.
              await new Promise(resolve => setTimeout(resolve, 500));
              recordResendWindow(
                resendGateRef,
                resendCooldownsRef,
                Date.now() + FALLBACK_RESEND_COOLDOWN_SECONDS * 1000,
                sendSubject
              );
              return;
            }
            // Bearer can disappear mid-session: readStoredStarfleetIdToken self-cleans
            // expired tokens, returning null. The OAuth bootstrap effect only fires on
            // mount, so we have to kick off silent re-auth ourselves here.
            if (!starfleetBearer) {
              handleUnauthorizedAndReload(intl);
            }
            try {
              const url = getPrhsApiRequestUrl(baseUrl, '/v1/register-phone');
              const res = await fetch(url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${starfleetBearer}`,
                },
                body: JSON.stringify({
                  phoneNumberAreaCode: cc,
                  phoneNumber: phoneNumberDigits,
                }),
              });
              if (res.status === 401) handleUnauthorizedAndReload(intl);
              if (!res.ok) {
                const errBody = await res.text();
                const errJson = parseJsonBody(errBody);
                console.error('[onSendCode] API error:', res.status, errBody);
                const errorCode = errJson?.errorCode || errJson?.error_code || errJson?.code;
                const isTooSoon = errorCode === RESEND_TOO_SOON_CODE;
                // Re-sync to whatever PRHS says is left, whichever status it
                // rides on, so a gate that was too short corrects itself and the
                // next countdown is right. A too-soon rejection arms the gate
                // even when the body doesn't say for how long: the refusal is
                // itself the signal to wait, and the module re-enables its
                // resend button on error without restarting the countdown, so
                // an unarmed gate here is the one path that would let the user
                // hammer the endpoint.
                const rejectedUntil =
                  readResendWindow(errJson) ??
                  (isTooSoon ? Date.now() + FALLBACK_RESEND_COOLDOWN_SECONDS * 1000 : null);
                if (rejectedUntil) {
                  recordResendWindow(
                    resendGateRef,
                    resendCooldownsRef,
                    rejectedUntil,
                    sendSubject
                  );
                }
                if (isTooSoon) {
                  // Zero only when PRHS named a window that has already passed
                  // (clock skew) — we have no honest number to show then.
                  const remaining = secondsUntil(rejectedUntil);
                  throw cooldownError(
                    remaining > 0
                      ? intl.formatMessage(
                          {
                            id: 'home.error.sendCode.resendTooSoon',
                            defaultMessage:
                              'Please wait {time} before requesting another code.',
                          },
                          { time: formatWaitTime(remaining) }
                        )
                      : intl.formatMessage({
                          id: 'home.error.sendCode.resendTooSoonUnknownWait',
                          defaultMessage:
                            'You recently requested a code. Please wait a moment before requesting another one.',
                        }),
                    remaining
                  );
                }
                const message = getHttpErrorMessage(intl, res.status, errBody, SEND_CODE_ERRORS);
                // PRHS spends a resend step on a send it refuses, so a refusal
                // of the number arrives with a window. The notice below owns
                // the banner for that window and would otherwise replace this
                // message — the only half the user can act on — with a
                // countdown, within half a second of it appearing.
                if (rejectedUntil) sendVerdictRef.current = message;
                throw httpError(message, errorCode);
              }
              // A code is now on its way, so the backoff starts. Prefer the
              // window PRHS returns; fall back to our conservative default when
              // the response doesn't carry one, since letting the module drop
              // back to its 60s tier is what caused the rejected resends.
              const sentUntil =
                readResendWindow(parseJsonBody(await res.text())) ??
                Date.now() + FALLBACK_RESEND_COOLDOWN_SECONDS * 1000;
              recordResendWindow(
                resendGateRef,
                resendCooldownsRef,
                sentUntil,
                sendSubject
              );
            } catch (err) {
              if (err instanceof TypeError) {
                console.error('[onSendCode] fetch failed:', err);
                throw new Error(getNetworkErrorMessage(intl, err));
              }
              throw err;
            }
          },
          onVerifyCode: async ({ code }) => {
            // Read the live intl (see onSendCode) so error copy is current-language.
            const intl = intlRef.current;
            // Modal renders 'verifying' spinner while this is pending; on resolve it
            // navigates (if onSuccessRedirectUrl returned by onInit) or closes.
            // Throw → modal returns to verify step with the error.
            const baseUrl = appConfigRef.current?.prhsApiUrl ?? process.env.REACT_APP_PRHS_API_URL;
            const starfleetBearer = getStarfleetBearerToken();
            const phoneNumber = phoneForVerifyRef.current;
            if (!baseUrl) {
              // Mock for local dev only (no PRHS API URL configured).
              await new Promise(resolve => setTimeout(resolve, 500));
              if (code?.length !== 6) {
                throw new Error(
                  intl.formatMessage({
                    id: 'home.error.invalidCodeClient',
                    defaultMessage: 'Invalid code. Please try again.',
                  })
                );
              }
              releaseResendWindow(resendGateRef, resendCooldownsRef);
              return;
            }
            // baseUrl is set → production path. Missing bearer here means the session
            // expired between Send Code and Verify (token was self-cleaned by
            // readStoredStarfleetIdToken). Trigger silent re-auth via reload — accepting
            // the OTP without server verification would be a critical security bug.
            if (!starfleetBearer) {
              handleUnauthorizedAndReload(intl);
            }
            // Missing phoneNumber means our internal ref was cleared — should never happen
            // in practice but throw rather than silently succeed against a fake number.
            if (!phoneNumber) {
              throw new Error(
                intl.formatMessage({
                  id: 'home.error.lostPhoneEntry',
                  defaultMessage: 'Lost your phone number entry. Please go back and resend the code.',
                })
              );
            }
            try {
              const url = getPrhsApiRequestUrl(baseUrl, '/v1/verify-otp');
              const res = await fetch(url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${starfleetBearer}`,
                },
                body: JSON.stringify({ phoneNumber, otp: code }),
              });
              if (res.status === 401) handleUnauthorizedAndReload(intl);
              if (!res.ok) {
                const errBody = await res.text();
                console.error('[onVerifyCode] API error:', res.status, errBody);
                // Wrong guesses are capped upstream, and past the cap every
                // further guess is refused without being checked. That is a wait
                // rather than a wrong code, so it gets the neutral countdown
                // treatment instead of a red "incorrect code" the user would
                // answer by guessing again.
                //
                // Gated on our own error code rather than the status, so a 429
                // from anywhere but PRHS is never read as the user mistyping.
                const errJson = parseJsonBody(errBody);
                const errorCode =
                  errJson?.errorCode || errJson?.error_code || errJson?.code;
                if (errorCode === VERIFY_RETRY_LIMIT_CODE) {
                  const waitSeconds = parseRetryAfterSeconds(res.headers?.get('Retry-After'));
                  throw cooldownError(
                    waitSeconds > 0
                      ? intl.formatMessage(
                          {
                            id: 'home.error.verify.retryLimit',
                            defaultMessage:
                              'Too many incorrect codes. Please wait {time} before trying again.',
                          },
                          { time: formatWaitTime(waitSeconds) }
                        )
                      : intl.formatMessage({
                          id: 'home.error.verify.retryLimitUnknownWait',
                          defaultMessage:
                            'Too many incorrect codes. Please wait a moment before trying again.',
                        }),
                    waitSeconds
                  );
                }
                throw httpError(
                  getHttpErrorMessage(intl, res.status, errBody, VERIFY_OTP_ERRORS),
                  errorCode
                );
              }
            } catch (err) {
              if (err instanceof TypeError) {
                console.error('[onVerifyCode] fetch failed:', err);
                throw new Error(getNetworkErrorMessage(intl, err));
              }
              throw err;
            }
            // Verified, so PRHS has dropped the resend record it was holding for
            // this account. Drop ours to match — see releaseResendWindow.
            releaseResendWindow(resendGateRef, resendCooldownsRef);
          },
          onSuccess: () => {
            // The module now owns the post-success UX: it renders its own success
            // page and (UAS only) the "Redirecting in Ns…" countdown + navigation.
            // Nothing for us to render. Clear the persisted UAS callback so a
            // back-navigation into this tab can't replay redirect logic on a fresh
            // visit. (The module already holds the redirect URL from onInit.)
            clearCallbackUrl();
            callbackUrlRef.current = null;
          },
          onCancel: () => {
            // Cancel only appears in edit mode (an existing number was prefilled
            // via initialPhoneNumber). The module's default action closes the tab
            // (window.close()); before that, drop any persisted UAS callback so a
            // later visit to this tab can't replay redirect logic from stale state.
            clearCallbackUrl();
            callbackUrlRef.current = null;
          },
          onClose: () => {
            // In our flows the module doesn't close itself on success (it stays on
            // the success page or navigates away), and user-dismiss is suppressed
            // (see modal.close override below). This stays as a safety net.
            modalRef.current = null;
          },
        });
    // Belt-and-suspenders against an accidental teardown. In inline mode the
    // module has no backdrop/Escape dismissal, so close() is only ever called by
    // our own post-success flow — but we still gate it behind allowModalCloseRef
    // (set true in onSuccess) so any stray close() can't blank the embedded form.
    // destroy() (used by this effect's cleanup) is untouched.
    allowModalCloseRef.current = false;
    const originalClose = modal.close.bind(modal);
    modal.close = () => {
      if (allowModalCloseRef.current) {
        originalClose();
      }
    };
    modalRef.current = modal;
    // open() awaits onInit (config + PRHS preflight). Keep the overlay spinner
    // until that finishes so Layout doesn't flash empty before the form paints.
    (async () => {
      try {
        await modal.open();
      } catch (err) {
        console.error('[Home] PhoneVerificationModal.open failed:', err);
      } finally {
        if (!cancelled && nothingToManageRef.current) {
          // Take the form back out. onInit can decline to return overrides but
          // not decline to be opened, so by now the module has rendered a phone
          // step into our container; leaving it there would undo the stop.
          // destroy() is deliberately not routed through the close() override
          // above, so this works while user-dismissal stays suppressed.
          if (modalRef.current && typeof modalRef.current.destroy === 'function') {
            modalRef.current.destroy();
          }
          modalRef.current = null;
          setNothingToManage(true);
        }
        if (!cancelled && !redirectingAwayRef.current) {
          setVerificationReady(true);
        }
      }
    })();
    // As-you-type phone formatting is now built into the module
    // (`formatPhoneAsYouType` option, defaults to libphonenumber-js). The verify
    // step also displays the full number formatted via the bundled
    // `formatPhoneNumber`. No consumer wiring needed.

    return () => {
      cancelled = true;
      if (modalRef.current && typeof modalRef.current.destroy === 'function') {
        modalRef.current.destroy();
      }
      modalRef.current = null;
    };
    // Construct the modal once for the authenticated session, NOT on locale.
    // Language changes are applied in place by the effect below via setIntl(),
    // which preserves the user's in-progress entry — recreating here would reset
    // the flow back to the empty phone step on every locale switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intl is read live via intlRef; modal is owned by this effect and (re)created only when auth flips.
  }, [isAuthenticated]);

  // Live locale switch: when the page language (and its async-loaded catalog)
  // changes, swap the module's message formatter in place instead of tearing the
  // modal down. The module re-renders the current step against the new strings
  // while carrying over the selected country, typed number, entered OTP digits,
  // and the running expiry/resend countdowns. We depend on the whole `intl`
  // object (stable per IntlProvider value) rather than `intl.locale` so the
  // re-render also fires when the matching catalog finishes loading after the
  // locale flips.
  useEffect(() => {
    modalRef.current?.setIntl(intl);
    // The country names are not part of the message catalog — they come from
    // Intl.DisplayNames over the PRHS codes — so setIntl cannot re-translate
    // them. Without this the list stays in whatever language the page was in
    // when the modal opened, which reads as a half-translated page. Cheap
    // enough to do here: the entries are already in memory, and setCountryList
    // carries the user's selection across by region.
    if (partnerCountriesRef.current) {
      modalRef.current?.setCountryList(
        toModuleCountryList(partnerCountriesRef.current, intl.locale)
      );
    }
  }, [intl]);

  // Give the phone step the countdown it doesn't have.
  //
  // The verify step shows "Request new code in {time}" while the resend
  // cooldown runs, but "Edit phone number" leaves that step and its timer
  // behind. On the phone step the only thing the module lets us drive is
  // validatePhoneNumber, which returns a bare boolean — so the user arrives to
  // a Send Code button that is correctly dead but says nothing about why, with
  // no way to tell a wait from a bug. This effect keeps the same wait visible
  // there by writing it into the module's error banner each tick.
  //
  // It also has to re-enable the button at zero. The module only recomputes
  // that state from user input (typing, changing country), so a user sitting
  // on the phone step when the window elapses would otherwise stay locked out
  // until they touched the field.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    // The module renders one step at a time inside our container, so there is
    // never more than one banner to find.
    const inModal = (selector) =>
      document.getElementById(VERIFICATION_CONTAINER_ID)?.querySelector(selector) ??
      null;

    const bannerText = () => inModal(SELECTORS.errorMessageText);

    // The banner is a role="alert" live region, so a value that changes every
    // second would have a screen reader restart the same sentence over and
    // over for the length of the cooldown, drowning out everything else. Let
    // the first render announce, then mute the region and keep updating the
    // text silently for sighted users. Muting is re-applied per tick because
    // any step re-render replaces the element with a fresh, unmuted one.
    const muteBanner = () => {
      const banner = inModal(SELECTORS.errorMessage);
      if (!banner) return;
      banner.setAttribute('aria-live', 'off');
      banner.removeAttribute('role');
    };

    const unmuteBanner = () => {
      const banner = inModal(SELECTORS.errorMessage);
      if (!banner) return;
      banner.setAttribute('aria-live', 'polite');
      banner.setAttribute('role', 'alert');
    };

    const clearNotice = () => {
      const modal = modalRef.current;
      if (!modal || cooldownNoticeRef.current === null) return;
      cooldownNoticeRef.current = null;
      // We only ever own the phone step's banner. Reaching here on another
      // step would mean the flow moved on mid-cooldown, which the disabled
      // Send Code button rules out today — but if that changes, dropping the
      // state without touching a banner we didn't write is the safe half.
      if (modal.state?.step !== 'phone') return;
      // Restore the live region first so the next genuine error announces.
      unmuteBanner();
      // Within the phone step the notice owns the banner outright for as long
      // as the cooldown runs — see the ownership test — so the only thing to
      // preserve is a refusal of the number itself, which outlives the wait it
      // arrived with: the number still needs replacing once the wait is over.
      const verdict = sendVerdictRef.current;
      sendVerdictRef.current = null;
      modal.setError(verdict ?? null);
      // Private to the module, hence the guard: if a future version drops it
      // the button simply stays disabled until the next keystroke instead of
      // throwing here.
      if (typeof modal.updateSendCodeButtonState === 'function') {
        modal.updateSendCodeButtonState();
      }
    };

    const tick = () => {
      const modal = modalRef.current;
      // Only the phone step. The verify step runs the module's own countdown,
      // and its banner is needed for verify errors.
      if (!modal || modal.state?.step !== 'phone') {
        clearNotice();
        return;
      }
      // Independent of what's in the field: the wait is the caller's, so
      // editing the number leaves the notice up rather than clearing it and
      // offering a Send Code that PRHS would only refuse.
      const remaining = resendWaitFor(resendGateRef.current);
      if (remaining <= 0) {
        clearNotice();
        return;
      }
      const wait = intlRef.current.formatMessage(
        {
          id: 'home.error.sendCode.resendTooSoon',
          defaultMessage: 'Please wait {time} before requesting another code.',
        },
        { time: formatWaitTime(remaining) }
      );
      // Verdict first, then the wait: the verdict is what the user acts on and
      // the wait is when they may act. Both are localized sentences, so they
      // read as one banner in any language.
      const message = sendVerdictRef.current
        ? `${sendVerdictRef.current} ${wait}`
        : wait;
      // Mute before every write except the notice's first, so exactly one
      // announcement goes out and the countdown that follows is silent.
      if (cooldownNoticeRef.current !== null) muteBanner();
      cooldownNoticeRef.current = message;
      // Writing only on change keeps this to roughly one DOM update a second
      // at a tick rate fast enough to look immediate. Comparing against what's
      // on screen (rather than what we last sent) also puts the notice back
      // after the module blanks the banner on keystroke.
      if (bannerText()?.textContent !== message) modal.setError(message);
    };

    const id = setInterval(tick, COOLDOWN_NOTICE_TICK_MS);
    return () => {
      clearInterval(id);
      cooldownNoticeRef.current = null;
    };
  }, [isAuthenticated]);

  // Say why the number stops being formatted when the country changes.
  //
  // libphonenumber only groups a number it recognises, so picking a country the
  // digits aren't valid for leaves the field showing them bare. Nothing marks
  // that moment: the number check runs in onSendCode, so the reason surfaces
  // only on a click the user has no reason to make yet, and until then the bare
  // digits read as the formatting having broken rather than as "this number
  // isn't a French one".
  //
  // Deliberately not folded into the cooldown ticker above: that runs several
  // times a second, which would flash this at someone still typing. It fires on
  // a country change and nothing else.
  //
  // The module has no country-change callback, so this listens for the radios'
  // own change event, delegated because each render replaces them. Bubble phase
  // (not capture) so the module's handler has already re-grouped the field and
  // moved `state.country` on by the time we read either.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handleCountryChange = event => {
      const container = document.getElementById(VERIFICATION_CONTAINER_ID);
      const target = event.target;
      if (!container || !(target instanceof Element)) return;
      if (!container.contains(target)) return;
      if (!target.matches(SELECTORS.countryDropdownItem)) return;

      const modal = modalRef.current;
      if (!modal || modal.state?.step !== 'phone') return;

      // The cooldown notice owns this banner outright while it runs and
      // rewrites it every tick, so anything put here would flicker and lose.
      if (resendWaitFor(resendGateRef.current) > 0) return;

      const bannerText =
        container.querySelector(SELECTORS.errorMessageText)?.textContent ?? null;
      const { countryCode, phoneNumber } = modal.getPhoneValues();
      const country = modal.state?.country;

      if (!phoneNumber.trim() || isValidPhoneNumber(country, countryCode, phoneNumber)) {
        // Take back only a banner still showing what we wrote: a send error
        // raised in between is the message that matters more.
        if (
          invalidNumberNoticeRef.current !== null &&
          bannerText === invalidNumberNoticeRef.current
        ) {
          modal.setError(null);
        }
        invalidNumberNoticeRef.current = null;
        return;
      }

      // Same wording onSendCode throws, so the explanation doesn't change
      // depending on whether the user waited to be told.
      const message = intlRef.current.formatMessage({
        id: 'home.error.invalidPhoneClient',
        defaultMessage:
          'This is not a valid phone number. Please check the country code and number format.',
      });
      invalidNumberNoticeRef.current = message;
      modal.setError(message);
    };

    document.addEventListener('change', handleCountryChange);
    return () => {
      document.removeEventListener('change', handleCountryChange);
      invalidNumberNoticeRef.current = null;
    };
  }, [isAuthenticated]);

  // Whether the session we're missing is one the visitor used to have. Only
  // changes the copy — both states are "no usable token" as far as the flow is
  // concerned.
  const sessionExpired = !isAuthenticated && didStarfleetSessionExpire();
  // A retry can only work if we know who to sign in as.
  const canRetrySignIn = Boolean(
    resolvedHintsRef.current && appConfigRef.current
  );

  /**
   * Close out of the "nothing to manage" screen — the same action the module's
   * edit-mode Cancel takes.
   *
   * No callback to clear on the way out, unlike that Cancel: this screen is
   * only reachable when neither the query string nor sessionStorage held one,
   * which is the very condition that identifies the manage surface.
   *
   * Browsers only honour close() for tabs a script opened, so for anyone who
   * followed an ordinary link here it does nothing. That silent no-op is the
   * dead end this screen exists to avoid, so when the tab survives the attempt
   * we say so instead. Going back isn't an option either: the Starfleet OAuth
   * roundtrip sits in this tab's history, so history.back() can land on the
   * callback and start the whole sign-in over.
   */
  const handleNothingToManageClose = () => {
    if (typeof window === 'undefined') return;
    try {
      window.close();
    } catch {
      // Some engines throw rather than ignore a refused close; same outcome.
    }
    if (!window.closed) setTabCloseRefused(true);
  };

  const handleSignInAgain = () => {
    const cfg = appConfigRef.current;
    const hints = resolvedHintsRef.current;
    if (!cfg || !hints || signingInAgain) return;
    const { clientId, loginOrigin } = resolveStarfleetOAuthSettings(cfg);
    if (!clientId || !loginOrigin) return;
    setSigningInAgain(true);
    // User-initiated, so the automatic budget resets: they get a fresh set of
    // silent attempts after this, and reaching this screen twice doesn't
    // permanently lock out retrying.
    clearReauthAttempts();
    Promise.resolve(
      startStarfleetAuthorize({ cfg, clientId, loginOrigin, hints })
    ).catch(() => {
      // Redirect never happened, so re-enable rather than leaving a dead button.
      setSigningInAgain(false);
    });
  };

  // Silent-login decision window: no token yet, and the auth bootstrap hasn't
  // ruled out a redirect to Starfleet. Render the same full-screen neutral
  // spinner as the OAuth callback — no page chrome and no "Sign in required"
  // copy — so the whole round-trip (initial load → redirect → callback → form)
  // reads as one continuous load instead of a sequence of flashing pages.
  if (!isAuthenticated && !signInResolved) {
    return (
      <LoadingScreen
        label={intl.formatMessage({
          id: 'home.signingIn',
          defaultMessage: 'Signing you in…',
        })}
      />
    );
  }

  return (
    <>
      {isAuthenticated && !verificationReady ? (
        <LoadingScreen
          overlay
          label={intl.formatMessage({
            id: 'home.signingIn',
            defaultMessage: 'Signing you in…',
          })}
        />
      ) : null}
      <Layout signedInUser={signedInUser}>
        <div className='page-container'>
          <div className="web-component-container">
            {!isAuthenticated ? (
              // Bootstrap finished and the visitor won't be signed in silently
              // (direct visit, cancelled login, or no usable hints): show the
              // manual sign-in prompt rather than a form they can't submit.
              <div className="demo-content">
                <h2>
                  {sessionExpired ? (
                    <FormattedMessage
                      id="home.sessionExpiredHeading"
                      defaultMessage="Your session has expired"
                    />
                  ) : (
                    <FormattedMessage
                      id="home.signInRequiredHeading"
                      defaultMessage="Sign in required"
                    />
                  )}
                </h2>
                <p>
                  {sessionExpired ? (
                    <FormattedMessage
                      id="home.sessionExpiredBody"
                      defaultMessage="For your security, this page stays signed in for a limited time. Sign in again to register your phone number, or start over from GeForce NOW."
                    />
                  ) : (
                    <FormattedMessage
                      id="home.signInRequiredBody"
                      defaultMessage="Please sign in to register your phone number."
                    />
                  )}
                </p>
                {canRetrySignIn ? (
                  <button
                    type="button"
                    className="verify-button"
                    onClick={handleSignInAgain}
                    disabled={signingInAgain}
                  >
                    {signingInAgain ? (
                      <FormattedMessage
                        id="home.signingIn"
                        defaultMessage="Signing you in…"
                      />
                    ) : (
                      <FormattedMessage
                        id="home.signInAgain"
                        defaultMessage="Sign in again"
                      />
                    )}
                  </button>
                ) : null}
              </div>
            ) : nothingToManage ? (
              // Manage-only entry for an account with no number on file.
              //
              // Rendered into the same containers as the form below, not just
              // the module's classes: .nv-dark carries the theme tokens the
              // card chrome is drawn from, and .verification-wrapper both
              // retargets the surface to the GFN-matched #2b2b2b and supplies
              // the 480px centred sizing and phone padding. Built from those
              // alone it inherited light-theme tokens at page width and looked
              // nothing like the flow's card.
              //
              // The mount-point id stays off — that belongs to the module — as
              // do the data-pv-* handles, since nothing here is the module's.
              <div className="nv-dark">
                <div className="verification-wrapper">
                  <div className={CUSTOM_CLASSES.inlineRoot}>
                    <div
                      className={CSS_CLASSES.modalContent}
                      role="group"
                      aria-labelledby={NOTHING_TO_MANAGE_HEADING_ID}
                    >
                      {/* The module wraps each step in this, so heading, body
                          and footer sit gap-density-lg apart rather than at the
                          card's wider 2xl. */}
                      <div
                        className={`${CSS_CLASSES.flex} ${CSS_CLASSES.flexCol} ${CSS_CLASSES.gapLg}`}
                      >
                        <h2
                          id={NOTHING_TO_MANAGE_HEADING_ID}
                          className={CSS_CLASSES.modalHeading}
                        >
                          <FormattedMessage
                            id="home.noPhoneToManageHeading"
                            defaultMessage="No phone number registered"
                          />
                        </h2>
                        <div className={CSS_CLASSES.modalMain}>
                          <p className={CUSTOM_CLASSES.description}>
                            <FormattedMessage
                              id="home.noPhoneToManageBody"
                              defaultMessage="You haven't added a phone number yet. You'll be able to manage it here once you do."
                            />
                          </p>
                          {tabCloseRefused ? (
                            <p
                              className={CUSTOM_CLASSES.dataRatesNotice}
                              role="status"
                            >
                              <FormattedMessage
                                id="home.noPhoneToManageCloseHint"
                                defaultMessage="You can close this tab now."
                              />
                            </p>
                          ) : null}
                        </div>
                        <div className={CSS_CLASSES.modalFooter}>
                        {/* Primary + brand, the Send Code treatment, rather
                            than Cancel's tertiary text: on this screen closing
                            is not the quiet alternative to a main action, it is
                            the only action there is. */}
                        <button
                          type="button"
                          className={`${CSS_CLASSES.button} ${CSS_CLASSES.buttonPrimary} ${CSS_CLASSES.buttonBrand}`}
                          onClick={handleNothingToManageClose}
                        >
                            <FormattedMessage
                              id="home.noPhoneToManageClose"
                              defaultMessage="Close"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Authenticated: the module renders the whole flow inline —
              // phone → verify → verifying → success (including, for UAS, its own
              // "Redirecting in Ns…" countdown). We no longer render a success
              // screen ourselves. Mount point stays in the DOM under the overlay
              // spinner so onInit can run before we reveal the form.
              <div className="nv-dark">
                <div
                  id={VERIFICATION_CONTAINER_ID}
                  className="verification-wrapper"
                />
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}

export default Home;
